/**
 * Low-level AES-256-GCM + PBKDF2-SHA256 primitives.
 *
 * Exported from this module for testing, but NOT re-exported from index.ts.
 * External consumers use buildContainer / parseContainer instead.
 *
 * All operations use the browser-native Web Crypto API (crypto.subtle).
 * Available in all modern browsers and Node.js 18+.
 */

/**
 * Derive a 256-bit AES-GCM key from a passphrase via PBKDF2-SHA256.
 *
 * @param passphrase - User-supplied passphrase. Never logged or stored.
 * @param salt       - 16 random bytes, per-file. Read from the container header on parse.
 * @param iterations - KDF iteration count. Use PBKDF2_ITERATIONS for real files;
 *                     lower values only in tests where speed matters.
 */
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * AES-256-GCM encrypt. Returns ciphertext + 16-byte auth tag (appended by Web Crypto).
 *
 * @param key  - Derived CryptoKey.
 * @param iv   - 12 random bytes. NEVER reuse with the same key.
 * @param data - Plaintext bytes to encrypt.
 */
export async function encryptBody(
  key: CryptoKey,
  iv: Uint8Array,
  data: Uint8Array,
): Promise<Uint8Array> {
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  return new Uint8Array(ciphertext);
}

/**
 * AES-256-GCM decrypt. Verifies the auth tag automatically.
 * Throws DOMException (OperationError) on any auth failure — wrong key, tampered
 * ciphertext, or truncated input. Callers map this to InvalidPassphraseError.
 *
 * @param key        - Derived CryptoKey.
 * @param iv         - The same 12-byte IV used during encryption.
 * @param ciphertext - Ciphertext + appended auth tag (as produced by encryptBody).
 */
export async function decryptBody(
  key: CryptoKey,
  iv: Uint8Array,
  ciphertext: Uint8Array,
): Promise<Uint8Array> {
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
  return new Uint8Array(plaintext);
}
